import {
  AdminNotificationData,
  AiVisibilityReportReadyNotificationData,
  AppNotification,
  CardExpiringNotificationData,
  PausedMonitorsNotificationData,
  PaymentReminderNotificationData,
  ProjectReadyNotificationData,
  resolveAdminNotification,
} from '../../types/notifications/Notification';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Single source of truth for a notification's human-readable text. Used both by the
 * NotificationCenter list items and the billing-issues tooltip, so the wording stays in sync.
 * `lang` selects the language variant for admin (free-text) notifications.
 */
export const getNotificationText = (
  t: TranslateFn,
  notification: AppNotification,
  lang = 'en'
): string => {
  switch (notification.type) {
    case 'PAUSED_MONITORS': {
      const data = notification.data as unknown as PausedMonitorsNotificationData;
      return t('notificationPausedMonitors', { count: data.count });
    }
    case 'CARD_EXPIRING': {
      const data = notification.data as unknown as CardExpiringNotificationData;
      return t('notificationCardExpiring', {
        date: `${String(data.expiryCardMonth).padStart(2, '0')}/${data.expiryCardYear}`,
      });
    }
    case 'PAYMENT_REMINDER': {
      const data = notification.data as unknown as PaymentReminderNotificationData;
      return t('notificationPaymentReminder', { date: new Date(data.endsAt).toLocaleDateString() });
    }
    case 'PROJECT_READY': {
      const data = notification.data as unknown as ProjectReadyNotificationData;
      return t('notificationProjectReady', { project: data.projectName });
    }
    case 'AI_VISIBILITY_REPORT_READY': {
      const data = notification.data as unknown as AiVisibilityReportReadyNotificationData;
      const period =
        data.periodLabel ||
        (data.periodFrom && data.periodTo ? `${data.periodFrom} – ${data.periodTo}` : '');
      return t('notificationAiVisibilityReportReady', {
        project: data.projectName,
        period,
      });
    }
    case 'ADMIN': {
      // Admin-authored free text — pick the current language variant.
      const data = notification.data as unknown as AdminNotificationData;
      return resolveAdminNotification(data, lang).title;
    }
    default:
      return '';
  }
};
